window.customElements.define(
  'custom-variant-picker-one-time',
  class CustomVariantPickerOneTime extends window.HTMLElement {
    connectedCallback() {
      this.initDOMElements();
      this.initEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('form');
      this.addToCartPrice = this.querySelector('#add-to-cart-price');
      this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
      this.errorMessage = this.querySelector('#error-message');
      this.addToCartButton = this.querySelector('#add-to-cart');
      this.notifyMeButton = this.querySelector('#notify-me-button');
      this.variants = this.form.querySelectorAll('.custom-variant-input');
      this.savings = this.querySelectorAll('#savings');
      this.savingsWrapper = this.querySelectorAll('#savings-wrapper');
      this.optionUnitPriceWrapper = this.querySelectorAll('#option-unit-price-wrapper');
      this.optionUnitPrices = this.querySelectorAll('#option-unit-price');
    }

    // Event Listeners
    initEventListeners() {
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('submit', this.handleAddToCartSubmit.bind(this));

      // Listen for sticky add to cart changes
      window.addEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange.bind(this));
    }

    removeEventListeners() {
      this.form.removeEventListener('change', this.handleChange.bind(this));
      this.form.removeEventListener('submit', this.handleAddToCartSubmit.bind(this));

      // Remove sticky add to cart listener
      window.removeEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange.bind(this));
    }

    // Event Handlers
    handleStickyAddToCartChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantInput = this.form.querySelector(`input[type="radio"][value="${variant.variantId}"]`);

      if (!variantInput) return;
      variantInput.checked = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-variant-picker:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              price: currentVariantData.variantPrice,
              compareAtPrice: currentVariantData.variantCompareAtPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleAddToCartSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const currentVariantData = this.getCurrentVariantData();
      const cartItem = this.buildCartItem(currentVariantData);

      try {
        this.setLoadingState(true);
        await this.addToCart(cartItem);
        this.dispatchCartRefresh();
        this.closeModalIfNeeded();
      } catch (error) {
        this.handleAddToCartError(error);
      } finally {
        this.setLoadingState(false);
        this.isSubmitting = false;
      }
    }

    buildCartItem(currentVariantData) {
      return {
        id: currentVariantData.variantId,
        quantity: 1,
      };
    }

    setLoadingState(isLoading) {
      this.form.classList.toggle('is-loading', isLoading);
    }

    handleAddToCartError(error) {
      console.error('Failed to add item to cart:', error);
      this.showErrorMessage(error.message);
    }

    showErrorMessage(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
      }
      this.errorMessage && this.errorMessage.classList.remove('hidden');

      setTimeout(() => {
        this.errorMessage && this.errorMessage.classList.add('hidden');
      }, 3000);
    }

    // UI Update Methods
    handleUpdateUI(currentVariantData) {
      this.updateButtonVisibility(currentVariantData);
      this.updatePriceDisplay(currentVariantData);
    }

    updateButtonVisibility(currentVariantData) {
      if (this.addToCartButton) {
        this.addToCartButton.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.notifyMeButton) {
        this.notifyMeButton.classList.toggle('hidden', currentVariantData.available);
      }
    }

    updatePriceDisplay(currentVariantData) {
      const { variantPrice, variantCompareAtPrice } = currentVariantData;

      this.setMainPrice(variantPrice);
      this.setCompareAtPrice(variantCompareAtPrice, variantPrice);
    }

    setMainPrice(price) {
      if (this.addToCartPrice) {
        this.addToCartPrice.textContent = price;
      }
    }

    setCompareAtPrice(compareAtPrice, mainPrice) {
      if (!this.addToCartCompareAtPrice) return;

      this.addToCartCompareAtPrice.textContent = compareAtPrice;

      if (compareAtPrice === mainPrice || !compareAtPrice) {
        this.addToCartCompareAtPrice.classList.add('hidden');
      } else {
        this.addToCartCompareAtPrice.classList.remove('hidden');
      }
    }

    handleUpdateUrl(currentVariantData) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // Cart and API Methods
    async addToCart(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();

      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    closeModalIfNeeded() {
      if (this.form.dataset.closeModalOnAdd === 'true') {
        const quickBuyDrawer = this.closest('quick-buy-drawer');
        if (quickBuyDrawer && typeof quickBuyDrawer.hide === 'function') {
          quickBuyDrawer.hide();
        }
      }
    }

    // Utility Methods
    getCurrentVariantData() {
      const currentVariant = this.form.querySelector('input[type="radio"]:checked');
      const { available, variantId, variantPrice, variantCompareAtPrice, variantSavings } = currentVariant.dataset;

      return {
        variantId,
        variantPrice,
        variantCompareAtPrice,
        variantSavings,
        available: available === 'true',
      };
    }
  }
);
